import { useState } from "react";
import { Download, Search, X } from "lucide-react";
import type { ItemDefinition } from "../types";
import {
  getSrdItem,
  previewToDefinition,
  searchSrdItems,
  type SrdItemPreview,
  type SrdResourceType,
  type SrdRuleset,
  type SrdSearchResult
} from "../lib/srdApi";

export function SrdItemImporter({
  onClose,
  onImport,
  newId,
  now
}: {
  onClose: () => void;
  onImport: (definition: ItemDefinition) => void;
  newId: () => string;
  now: () => string;
}) {
  const [ruleset, setRuleset] = useState<SrdRuleset>("2024");
  const [resourceType, setResourceType] = useState<SrdResourceType>("magic-items");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SrdSearchResult[]>([]);
  const [preview, setPreview] = useState<SrdItemPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function search() {
    setBusy(true); setError(""); setPreview(null);
    try {
      setResults(await searchSrdItems(query, ruleset, resourceType));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to reach the SRD API.");
    } finally {
      setBusy(false);
    }
  }

  async function choose(result: SrdSearchResult) {
    setBusy(true); setError("");
    try {
      setPreview(await getSrdItem(result.index, ruleset, resourceType));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to load that item.");
    } finally {
      setBusy(false);
    }
  }

  function doImport() {
    if (!preview) return;
    onImport(previewToDefinition(preview, newId(), now()));
  }

  return <div className="modal-backdrop" onMouseDown={onClose}>
    <div className="modal srd-modal" onMouseDown={e=>e.stopPropagation()}>
      <div className="modal-head">
        <div>
          <div className="eyebrow">OPEN SRD SOURCE</div>
          <h2>Import Item from SRD</h2>
        </div>
        <button className="icon-btn" onClick={onClose}><X size={18}/></button>
      </div>

      <div className="notice">
        Imports create a normal Worldwarden Item Definition. Future changes to the remote source will not overwrite your copy.
      </div>

      <div className="form-grid">
        <label className="field"><span>Rules source</span>
          <select value={ruleset} onChange={e=>{setRuleset(e.target.value as SrdRuleset);setResults([]);setPreview(null)}}>
            <option value="2024">D&D 2024 — SRD 5.2</option>
            <option value="2014">D&D 2014 — SRD 5.1</option>
          </select>
        </label>
        <label className="field"><span>Resource</span>
          <select value={resourceType} onChange={e=>{setResourceType(e.target.value as SrdResourceType);setResults([]);setPreview(null)}}>
            <option value="magic-items">Magic Items</option>
            <option value="equipment">Equipment</option>
          </select>
        </label>
      </div>

      <div className="srd-search">
        <input
          value={query}
          onChange={e=>setQuery(e.target.value)}
          onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();search()}}}
          placeholder='Try "greater healing", "adamantine", "rope"...'
        />
        <button className="primary" onClick={search} disabled={busy}><Search size={16}/>{busy?"Loading...":"Search"}</button>
      </div>

      {error && <div className="error-box">{error}</div>}

      <div className="srd-browser">
        <div className="srd-results">
          <div className="srd-pane-title">Search results <span>{results.length}</span></div>
          {!results.length && <p className="muted">Search the selected SRD collection.</p>}
          {results.map(r=><button className="srd-result" key={`${r.resourceType}-${r.index}`} onClick={()=>choose(r)}>
            <strong>{r.name}</strong><span>{r.index}</span>
          </button>)}
        </div>

        <div className="srd-preview">
          <div className="srd-pane-title">Preview</div>
          {!preview ? <div className="srd-preview-empty">Select an item to preview the SRD record before importing.</div> :
          <>
            <div className="entity-top"><span>{preview.category.replaceAll("_"," ")}</span><span>{preview.ruleset} SRD</span></div>
            <h3>{preview.name}</h3>
            <div className="srd-meta">
              <span><b>Rarity</b>{preview.rarity||"—"}</span>
              <span><b>Magical</b>{preview.magical?"Yes":"No"}</span>
              <span><b>Attunement</b>{preview.requiresAttunement?"Required":"No"}</span>
              <span><b>Consumable</b>{preview.consumable?"Yes":"No"}</span>
            </div>
            <p className="srd-copy">{preview.mechanics||preview.summary}</p>
            <div className="source-line">Source: D&D {preview.ruleset} SRD via dnd5eapi.co</div>
            <button className="primary srd-import" onClick={doImport}><Download size={16}/>Import into Item Master</button>
          </>}
        </div>
      </div>
    </div>
  </div>
}
